import { JSX, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Bar, BarChart, BarShapeProps, LabelList, Legend, ReferenceLine, Rectangle, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { QesColors } from "@app/shared/qes-standards/colors";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import { NetworkIntegrityProviderTypes } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityAdequacyImpactType, NetworkIntegrityJobStatus } from "@app/projects/enumerators";
import { NetworkIntegrityAdequacyImpactResult } from "@app/projects/network-assessment/network-integrity/models";
import { NetworkIntegrityWidgetInfoFlyout } from "@app/projects/network-assessment/network-integrity/widgets/NetworkIntegrityWidgetInfoFlyout";
import { RoundedLegendIcon } from "@app/projects/network-assessment/network-integrity/widgets/RoundedLegendIcon";
import { WidgetLoading, WidgetHeader, Widget, WidgetBody } from "@app/projects/network-assessment/network-integrity/widgets/Widget";
import { WidgetMenu } from "@app/projects/network-assessment/network-integrity/widgets/WidgetMenu";
import { AdequacyImpactDescription } from "@app/projects/network-assessment/network-integrity/widgets/descriptions/AdequacyImpactDescription";
import { formatDate } from "@app/shared/formatDateTime";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

interface AdequacyImpactChartData {
  barName: string;
  barValue: number;
  color: string;
  impactType: NetworkIntegrityAdequacyImpactType;
  legendName: string;
  legendValue: number;
};

interface AdequacyImpactScenarioBar {
  barName: string;
  impactType: NetworkIntegrityAdequacyImpactType;
  total: number;
  baseline: number;
  newGaps: number;
};

const renderLegendText = (value: ReactNode) => {
  return (
    <span className="ni-adequacy-impact__legend-item-text">
      {value}
    </span>
  );
};

const renderLegendIcon = (icon: JSX.Element) => (
  <svg width={13} height={13} viewBox={"0 0 32 32"} style={{ display: "inline-block", flexShrink: 0, marginRight: 6 }}>
    {icon}
  </svg>
);

const AdequacyImpactWidget = (): JSX.Element => {
  const title = "Adequacy Impact";

  const history = useHistory();
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const ffContext = useContext(FeatureFlagContext);
  const project = networkIntegrityContext.project!;
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);
  const criticalAlertsEnabled = ffContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_ADEQUACY_IMPACT_CRITICAL_ALERTS);

  const [loading, setLoading] = useState<boolean>(false);
  const [resultLoaded, setResultLoaded] = useState<boolean>(false);
  const [adequacyImpactResult, setAdequacyImpactResult] = useState<NetworkIntegrityAdequacyImpactResult>(null);

  useEffect(() => {
    let cancelled = false;
    setAdequacyImpactResult(null);
    setResultLoaded(false);
    async function fetchProviderBreakdownResults() {
      try {
        if (networkIntegrityContext.providerType === NetworkIntegrityProviderTypes.Prospect ||
          networkIntegrityContext.providerType === NetworkIntegrityProviderTypes.ProspectServicing) {
          if (!cancelled) setAdequacyImpactResult(null);
        } else if (networkIntegrityContext.projectRunConfigurationId) {
          const result = await networkIntegrityService.getAdequacyImpactResult(project.projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes);
          if (!cancelled) {
            setAdequacyImpactResult(result);
            setResultLoaded(true);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (networkIntegrityContext.isJobStatusLoading === false
      && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished
      && !networkIntegrityContext.isAdequacyImpactProcessing) {
      setLoading(true);
      fetchProviderBreakdownResults().catch(() => { if (!cancelled) setResultLoaded(false); });
    }

    return () => { cancelled = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.jobStatus, networkIntegrityContext.isAdequacyImpactProcessing, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes]);

  const chartData: AdequacyImpactChartData[] = [
    {
      barName: "Current Gaps",
      barValue: adequacyImpactResult?.currentGapCount || 0,
      color: QesColors.QesTertiaryPurple3,
      impactType: NetworkIntegrityAdequacyImpactType.CurrentGaps,
      legendName: "Current Adequacy Gaps",
      legendValue: 0
    },
    {
      barName: "Alerts Removed",
      barValue: adequacyImpactResult?.alertsRemovedCount || 0,
      color: QesColors.QesTertiaryPurple1,
      impactType: NetworkIntegrityAdequacyImpactType.AlertGaps,
      legendName: "Alerts Removed",
      legendValue: 0
    },
    {
      barName: "Alerts and Warnings Removed",
      barValue: adequacyImpactResult?.alertsAndWarningsRemovedCount || 0,
      color: "url(#ni-adequacy-impact-stripe-pattern)",
      impactType: NetworkIntegrityAdequacyImpactType.AlertOrWarningGaps,
      legendName: "Alerts and Warnings Removed",
      legendValue: 0
    }
  ];

  const currentGapCount = adequacyImpactResult?.currentGapCount || 0;

  // Scenario bars for the Critical Alerts (baseline) design.
  const scenarioBars = useMemo<AdequacyImpactScenarioBar[]>(() => {
    if (!adequacyImpactResult) return [];

    const buildBar = (total: number, impactType: NetworkIntegrityAdequacyImpactType, barName: string): AdequacyImpactScenarioBar => ({
      barName,
      baseline: Math.min(total, currentGapCount),
      impactType,
      newGaps: Math.max(0, total - currentGapCount),
      total
    });

    const bars: AdequacyImpactScenarioBar[] = [
      buildBar(adequacyImpactResult.criticalAlertsRemovedCount || 0, NetworkIntegrityAdequacyImpactType.CriticalAlertGaps, "Critical Alerts Removed"),
      buildBar(adequacyImpactResult.alertsRemovedCount || 0, NetworkIntegrityAdequacyImpactType.AlertGaps, "All Alerts Removed"),
      buildBar(adequacyImpactResult.alertsAndWarningsRemovedCount || 0, NetworkIntegrityAdequacyImpactType.AlertOrWarningGaps, "Alerts and Warnings Removed")
    ];

    return bars;
  }, [adequacyImpactResult, currentGapCount]);

  const renderCriticalAlertsLegend = () => (
    <div className="d-flex flex-column ni-adequacy-impact__legend">
      <div className="ni-adequacy-impact__legend-item">
        {renderLegendIcon(<line x1={0} y1={16} x2={32} y2={16} stroke={QesColors.QesTertiaryPurple5} strokeWidth={4} strokeDasharray="5 4" />)}
        {renderLegendText(<>Current<br />Gaps:<br /><b>{currentGapCount.toLocaleString()}</b></>)}
      </div>
      <div className="ni-adequacy-impact__legend-item">
        {renderLegendIcon(<RoundedLegendIcon fill={QesColors.QesTertiaryPurple4} />)}
        {renderLegendText(<>New<br />Gaps</>)}
      </div>
      <div className="ni-adequacy-impact__legend-item">
        {renderLegendIcon(<RoundedLegendIcon fill={QesColors.QesTertiaryPurple6} />)}
        {renderLegendText(<>Current<br />Gaps</>)}
      </div>
    </div>
  );

  const calculateDomainValue = (value: number): number => {
    return (value >= 100)
      ? Math.ceil(value / 100) * 100
      : Math.ceil(value / 10) * 10;
  }

  // Calculate dynamic Y-axis ticks. The baseline design must also fit the current-gaps line.
  const barValues = criticalAlertsEnabled
    ? [currentGapCount, ...scenarioBars.map(b => b.total)]
    : chartData.map(item => item.barValue);
  const maxValue = adequacyImpactResult ? Math.max(...barValues, 0) : 100; // Default max value to 100 if no data
  let maxDomain = calculateDomainValue(maxValue);

  // Add extra space (15%) above the highest bar to prevent label cutoff
  const labelPadding = Math.ceil(maxDomain * 0.15);
  maxDomain = maxDomain + labelPadding;

  if (adequacyImpactResult?.isOutOfService)
    return (
      <Widget additionalClassNames="ni-adequacy-impact">
        <WidgetHeader title={title} />
        <WidgetBody>
          <div className="ni-adequacy-impact__unavailable">
            <p>Adequacy Impact results are not available prior to {formatDate(adequacyImpactResult.adequacyImpactOutOfServiceDate)}</p>
          </div>
        </WidgetBody>
      </Widget>
    );

  if (networkIntegrityContext.isLoading || loading || !networkIntegrityContext.jobStatus)
    return (<WidgetLoading additionalClassNames="ni-adequacy-impact"><WidgetHeader title={title} /></WidgetLoading>);

  const isProspectScope = networkIntegrityContext.providerType === NetworkIntegrityProviderTypes.Prospect
    || networkIntegrityContext.providerType === NetworkIntegrityProviderTypes.ProspectServicing;

  const isOpportunityScope = networkIntegrityContext.isOpportunityServiceArea
    || adequacyImpactResult?.isOpportunityOnlySelection;

  const hasNoGapResults = resultLoaded
    && networkIntegrityContext.isAdequacyImpactExpected
    && !adequacyImpactResult;

  const placeholder = isProspectScope
    ? { header: "No Impact", text: "Removing prospect provider access points does not have an impact on overall gap counts." }
    : isOpportunityScope
      ? { header: "No Impact", text: "Adequacy gap analysis is not performed for Opportunity service areas." }
      : hasNoGapResults
        ? { header: "No Adequacy Gaps Identified", text: "This network has no current gaps, and no new gaps open when providers with integrity alerts or warnings are removed." }
        : null;

  const handleBarClick = (impactType: NetworkIntegrityAdequacyImpactType) => {
    if (impactType === NetworkIntegrityAdequacyImpactType.CurrentGaps) return;
    history.push(
      `/projects/${project.projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/adequacy-impact/${networkIntegrityContext.providerType}/${impactType}`,
      {
        initialRegionSetIds: networkIntegrityContext.regionSetIds ?? [],
        initialSpecialtyCodes: networkIntegrityContext.specialtyCodes ?? []
      }
    );
  };

  const renderScenarioBarLabel = (props: { x?: number, y?: number, width?: number, index?: number }) => {
    const bar = scenarioBars[props.index ?? -1];
    if (!bar || props.x == null || props.y == null || props.width == null) return null;
    const centerX = props.x + props.width / 2;
    return (
      <g>
        <text x={centerX} y={props.y - 14} textAnchor="middle" className="ni-adequacy-impact__bar-total">{bar.total.toLocaleString()}</text>
        {bar.newGaps > 0 &&
          <text x={centerX} y={props.y - 3} textAnchor="middle" className="ni-adequacy-impact__bar-delta">+{bar.newGaps.toLocaleString()} new</text>}
      </g>
    );
  };

  const renderCriticalAlertsChart = () => (
    <div className="d-flex flex-row ni-adequacy-impact__baseline-container">
      <div className="d-flex flex-column w-100">
        {adequacyImpactResult ?
          <ResponsiveContainer>
            <BarChart data={scenarioBars} margin={{ bottom: 0, right: 0, top: 10 }} className="ni-adequacy-impact__scenario-chart">
              <Legend
                align="left"
                layout="vertical"
                verticalAlign="top"
                content={renderCriticalAlertsLegend()}
              />
              <XAxis dataKey="barName" tickLine={false}
                interval={0}
                tick={{
                  className: "demibold color-logan ni-adequacy-impact__empty-trend-axis",
                  textAnchor: 'middle',
                  width: 80,
                  wordSpacing: 'break-word'
                }}
                height={45}
              />
              <YAxis domain={[0, maxDomain]} tickLine={false} width={10} tick={{ className: "ni-adequacy-impact__empty-trend-axis" }} />
              <Bar
                dataKey="baseline" stackId="gaps" barSize={38}
                shape={(shapeProps: BarShapeProps & { payload: AdequacyImpactScenarioBar }) => (
                  <Rectangle {...shapeProps} fill={QesColors.QesTertiaryPurple6} cursor={"pointer"} onClick={() => handleBarClick(shapeProps.payload.impactType)} />
                )}
              />
              <Bar
                dataKey="newGaps" stackId="gaps" barSize={38}
                shape={(shapeProps: BarShapeProps & { payload: AdequacyImpactScenarioBar }) => (
                  <Rectangle {...shapeProps} fill={QesColors.QesTertiaryPurple4} cursor={"pointer"} onClick={() => handleBarClick(shapeProps.payload.impactType)} />
                )}
              >
                <LabelList dataKey="newGaps" content={renderScenarioBarLabel} />
              </Bar>
              <ReferenceLine y={currentGapCount} stroke={QesColors.QesTertiaryPurple5} strokeDasharray="5 4" strokeWidth={2} ifOverflow="extendDomain" />
            </BarChart>
          </ResponsiveContainer>
          :
          <div className="ni-adequacy-impact__no-data color-logan demibold">-- No Data --</div>
        }
      </div>
    </div>
  );

  const renderLegacyLegend = () => (
    <div>
      {chartData.map((legendItem) => (
        <div key={legendItem.legendName} className="ni-adequacy-impact__legend-item">
          {renderLegendIcon(<RoundedLegendIcon fill={legendItem.color} />)}
          {renderLegendText(legendItem.legendName)}
        </div>
      ))}
    </div>
  );

  const renderLegacyChart = () => (
    <div className="d-flex flex-row ni-adequacy-impact__chart-container">
      <div className="d-flex flex-column w-50">
        <ResponsiveContainer>
          <BarChart data={chartData}>
            <svg width="100%" height="100%">
              <defs>
                <pattern id="ni-adequacy-impact-stripe-pattern" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" width="4" height="4">
                  <line x1="0" y1="0" x2="0" y2="10" stroke={QesColors.QesTertiaryPurple2} strokeWidth="4" />
                </pattern>
              </defs>
            </svg>
            {chartData.map((legendItem, legendIndex) => (
              <Bar key={legendIndex} dataKey="legendValue" fill={legendItem.color} name={legendItem.legendName} />
            ))}
            <Legend
              align="left"
              wrapperStyle={{
                top: "25%",
                whiteSpace: "break-spaces",
              }}
              content={renderLegacyLegend()}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="d-flex flex-column w-50">
        <div>
          <ResponsiveContainer
            height="60%"
            width="77%"
            style={{
              bottom: 10,
              position: "absolute",
              right: 15,
            }}
          >
            <BarChart data={chartData} margin={{ bottom: 0, right: 0, top: 10 }}>
              <XAxis dataKey="barName" tickLine={false}
                interval={0}
                tick={{
                  className: "demibold color-logan ni-adequacy-impact__empty-trend-axis",
                  textAnchor: 'middle',
                  width: 80,
                  wordSpacing: 'break-word'
                }}
                height={45}
              />
              <YAxis domain={[0, maxDomain]} tickLine={false} tick={{ className: "ni-adequacy-impact__empty-trend-axis" }} />
              {adequacyImpactResult ?
                <>
                  <Bar
                    dataKey="barValue" barSize={38}
                    shape={(shapeProps: BarShapeProps & { payload: AdequacyImpactChartData }) => (
                      <Rectangle
                        {...shapeProps}
                        fill={shapeProps.payload.color}
                        cursor={shapeProps.payload.impactType !== NetworkIntegrityAdequacyImpactType.CurrentGaps ? "pointer" : ""}
                        onClick={() => handleBarClick(shapeProps.payload.impactType)}
                      />
                    )}
                  >
                    <LabelList dataKey="barValue" position="top" style={{ fill: 'black', fontSize: "10px", fontWeight: 'bold' }} />
                  </Bar>
                </>
                :
                <text x="50%" y="45%" className="color-logan demibold">
                  -- No Data --
                </text>
              }
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  return (networkIntegrityContext.isJobStatusLoading === false &&
    <Widget additionalClassNames="ni-adequacy-impact">
      <WidgetHeader title={title}>
        <WidgetMenu>
          <NetworkIntegrityWidgetInfoFlyout title="Adequacy Impact" description={<AdequacyImpactDescription />} />
        </WidgetMenu>
      </WidgetHeader>
      <WidgetBody>
        {placeholder
          ? <div className="ni-adequacy-impact__placeholder" data-testid="ni-adequacy-impact-placeholder">
              <h4>{placeholder.header}</h4>
              <p>{placeholder.text}</p>
            </div>
          : criticalAlertsEnabled ? renderCriticalAlertsChart() : renderLegacyChart()}
      </WidgetBody>
    </Widget>
  );
};

export { AdequacyImpactWidget };
